import React from 'react';

import { Attribution, ErrorBoundary } from '@confluence/error-boundary';
import { ConditionalCreateSpaceDrawer } from '@confluence/create-space/entry-points/createSpace';
import {
	ConditionalImportContentModal,
	ConditionalImportContentDrawer,
} from '@confluence/import-content/entry-points/importContent';
import { ForgeGlobalKeyboardShortcuts } from '@confluence/forge-ui';
import { StorageEnforcement } from '@confluence/storage-enforcement/entry-points/StorageEnforcement';
import { OnboardingQuickstart } from '@confluence/onboarding-quickstart';
import { RovoVideoModal } from '@confluence/rovo-video-modal/entry-points/RovoVideoModal';
import { TrialFlags } from '@confluence/trial-components';
import { LazyRestrictionsDialogMountPoint } from '@confluence/restrictions';
import { ProgressBar } from '@confluence/global-progress-bar';
import {
	useGetNav4OptIn,
	useIsNav4Enabled,
	useAddUFOCustomDataForNavType,
} from '@confluence/nav4-enabled';
import { Nav4FeedbackCollectorModal } from '@confluence/nav4-feedback';
import { MisalignedNavVersionsFlag } from '@confluence/nav-versions-misaligned';
import { TrialPlanComparisonModalMountComponent } from '@confluence/persistent-upgrade';
import { ModernizeModal } from '@confluence/modernize-confluence-ipm-modal/entry-points/ModernizeModal';
import { StandardToPremiumModalMountComponent } from '@confluence/experiment-edition-awareness-for-standard-to-premium';
import { ModernizeChangeboarding } from '@confluence/modernize-changeboarding';
/**
 * Use this component to render global components that should be rendered on every page.
 */
export const GlobalComponents = () => {
	const { nav4AdminOptedIn: isSiteWideNav4Enabled } = useGetNav4OptIn();
	const isNav4DisplayedToUser = useIsNav4Enabled();

	useAddUFOCustomDataForNavType();

	return (
		<>
			<ErrorBoundary
				attribution={Attribution.DISCO}
				attributes={{
					errorBoundaryId: 'GlobalComponents-Disco',
				}}
			>
				<ConditionalCreateSpaceDrawer />
				{/* NOTE: Nav4FeedbackCollectorModal can also be viewed when current user sees Nav3.
				The scenario is when confluence_nav_4_phase_1_opt_out FG is enabled, where
				the personal opt in is false (user sees nav3) and site-wide opt in is true*/}
				{(isSiteWideNav4Enabled || isNav4DisplayedToUser) && <Nav4FeedbackCollectorModal />}
				<MisalignedNavVersionsFlag />
			</ErrorBoundary>

			<ErrorBoundary
				attribution={Attribution.MIGRATIONS}
				attributes={{
					errorBoundaryId: 'GlobalComponents-Migrations',
				}}
			>
				<ConditionalImportContentModal />
				<ConditionalImportContentDrawer />
			</ErrorBoundary>

			<ErrorBoundary
				attribution={Attribution.ECOSYSTEM}
				attributes={{
					errorBoundaryId: 'GlobalComponents-Ecosystem',
				}}
			>
				<ForgeGlobalKeyboardShortcuts />
			</ErrorBoundary>

			<ErrorBoundary
				attribution={Attribution.ADMIN_EXPERIENCE}
				attributes={{
					errorBoundaryId: 'GlobalComponents-AdminExperience',
				}}
			>
				<StorageEnforcement />
			</ErrorBoundary>

			<ErrorBoundary
				attribution={Attribution.CC_ONBOARDING}
				attributes={{
					errorBoundaryId: 'GlobalComponents-Onboarding',
				}}
			>
				<OnboardingQuickstart />
				<ModernizeModal />
			</ErrorBoundary>

			<ErrorBoundary
				attribution={Attribution.GROWTH_LUNA}
				attributes={{
					errorBoundaryId: 'GlobalComponents-GrowthLuna',
				}}
			>
				<TrialPlanComparisonModalMountComponent />
				<StandardToPremiumModalMountComponent />
				<RovoVideoModal />
				<TrialFlags />
			</ErrorBoundary>

			<ErrorBoundary attribution={Attribution.PERMISSIONS_EXPERIENCE}>
				<LazyRestrictionsDialogMountPoint />
			</ErrorBoundary>

			<ErrorBoundary attribution={Attribution.CC_ONBOARDING}>
				<ModernizeChangeboarding />
			</ErrorBoundary>

			{isNav4DisplayedToUser && (
				<ErrorBoundary attribution={Attribution.BACKBONE}>
					<ProgressBar isNav4 />
				</ErrorBoundary>
			)}
		</>
	);
};
